import { useEffect, useState } from "react"
import { Link, NavLink, Outlet, useLocation } from "react-router-dom"
import { Bell, CalendarDays, Inbox, LogOut, Palette, Plus, Settings as SettingsIcon, Sparkles, Users } from "lucide-react"
import { fetchNotifications } from "~/apis/notifications"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useAuth } from "~/AuthContext"
import routes from "~/routes"

const NAV = [
  { to: routes.home, label: "Calendar", icon: CalendarDays, end: true },
  { to: routes.cleans, label: "Cleans", icon: Sparkles },
  { to: routes.residencies, label: "Residencies", icon: Palette },
  { to: routes.notifications, label: "Inbox", icon: Inbox },
  { to: routes.cleaners, label: "Cleaners", icon: Users },
  { to: routes.settings, label: "Settings", icon: SettingsIcon },
]

// Polls the inbox count every minute (and on every route change) so the bell
// and the mobile Inbox tab show what's waiting.
function useUnreadCount() {
  const [ count, setCount ] = useState(0)
  const location = useLocation()

  useEffect(() => {
    let cancelled = false
    const refresh = () => fetchNotifications().then((data) => { if (!cancelled) setCount(data.unread_count ?? 0) }).catch(() => {})
    refresh()
    const timer = setInterval(refresh, 60_000)
    return () => { cancelled = true; clearInterval(timer) }
  }, [ location.pathname ])

  return count
}

function UnreadBadge({ count, className }) {
  if (!count) return null
  return (
    <span className={cn("inline-flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-4 text-white", className)}>
      {count > 99 ? "99+" : count}
    </span>
  )
}

export default function Layout() {
  const { signOut } = useAuth()
  const unread = useUnreadCount()

  return (
    // Bottom padding on mobile keeps page content clear of the fixed tab bar.
    <div className="min-h-screen bg-background pb-20 text-foreground md:pb-8">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-1 px-4">
          <Link to={routes.home} className="mr-4 shrink-0 font-bold">Jacky Winter Places</Link>

          <nav className="hidden flex-1 items-center gap-1 md:flex">
            {NAV.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg px-3 py-1.5 text-sm font-medium",
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1 md:flex-none" />

          <Button asChild size="sm" className="hidden bg-brand text-brand-foreground hover:bg-brand/90 md:inline-flex">
            <Link to={routes.newBooking}>
              <Plus /> Booking
            </Link>
          </Button>
          <Button asChild variant="ghost" size="icon-sm" className="relative" aria-label={unread ? `${unread} unread notifications` : "Notifications"} title="Inbox">
            <Link to={routes.notifications}>
              <Bell />
              <UnreadBadge count={unread} className="absolute -right-0.5 -top-0.5" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={signOut} aria-label="Sign out" title="Sign out">
            <LogOut />
          </Button>
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      {/* Mobile bottom tabs */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-background pb-[env(safe-area-inset-bottom)] md:hidden">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn("flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium", isActive ? "text-brand" : "text-muted-foreground")
            }
          >
            <span className="relative">
              <Icon size={20} />
              {to === routes.notifications && <UnreadBadge count={unread} className="absolute -right-2.5 -top-1.5" />}
            </span>
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
